import { RefObject, useMemo, useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { formatISO } from 'date-fns';
import { Button as PrimeButton } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { ScheduledSemenItem } from '@/services/semen-pull';

import {
  usePullSemen,
  useRejectDiscardSemen,
  useReturnSemen,
} from '../../services/semen-pull/hooks';
import { OpenODSemenDialog } from './dialogs/OpenODSemenDialog';
import { SslQcDialog } from './dialogs/SslQcDialog';
import { GenerateBullLabels } from './GenerateBullLabels';
import { useSemenPullContext } from './useSemenPullContext';

const SSL_QC_SEMEN_TYPES = ['RS F', 'RS M'];

interface SemenPullActionsProps {
  scheduledGridRef: RefObject<AgGridReact<ScheduledSemenItem>>;
}

export const SemenPullActions = ({
  scheduledGridRef,
}: SemenPullActionsProps) => {
  const { t } = useTranslation();
  const {
    semenState,
    scheduledSemen,
    pulledSemen,
    refetchScheduledSemen,
    refetchPulledSemen,
  } = useSemenPullContext();
  const [params] = useSearchParams();
  const selectedDate = params.get('date');
  const selectedSiteId = params.get('site');
  const [rejectionDescription, setRejectionDescription] = useState('');
  const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
  const [openODSemenDialogVisible, setOpenODSemenDialogVisible] =
    useState(false);
  const [sslQcDialogVisible, setSslQcDialogVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pendingMove, setPendingMove] = useState<'pull' | 'return' | null>(
    null,
  );
  const moveInProgress = useRef(false);

  const sslQcEnabled = useFeatureFlag('SSL_QC_Entry');
  const sslQcScheduledAvailabilityEnabled = useFeatureFlag(
    'ssl_qc_scheduled_availability',
  );
  const sequentialMovesEnabled = useFeatureFlag('Semen_Sequential_Moves');
  const isMovePending = sequentialMovesEnabled && pendingMove !== null;

  const hasScheduledRsSemenTypes = useMemo(
    () =>
      (scheduledSemen ?? []).some(
        (row) =>
          !!row.semenId &&
          SSL_QC_SEMEN_TYPES.includes(
            row.semenTypeName?.trim().replace(/^\./, '') ?? '',
          ),
      ),
    [scheduledSemen],
  );

  const hasLegacyRsSemenTypes = useMemo(() => {
    const allSemen = [...(scheduledSemen ?? []), ...(pulledSemen ?? [])];
    return allSemen.some(
      (row) => row.semenTypeName === 'RS F' || row.semenTypeName === 'RS M',
    );
  }, [scheduledSemen, pulledSemen]);

  const hasRsSemenTypes = sslQcScheduledAvailabilityEnabled
    ? hasScheduledRsSemenTypes
    : hasLegacyRsSemenTypes;

  const pullSemenMutation = usePullSemen();
  const returnSemenMutation = useReturnSemen();
  const rejectDiscardSemenMutation = useRejectDiscardSemen();

  const handleExportSemen = async () => {
    setIsExporting(true);
    try {
      if (scheduledGridRef.current) {
        const scheduledGridApi = scheduledGridRef.current.api;
        const allColumns = scheduledGridApi.getColumns();
        if ((allColumns?.length as number) > 0) {
          const firstColumnId = allColumns?.[0].getColId();
          await scheduledGridApi.setColumnsVisible(
            [firstColumnId as string],
            false,
          );
          const csvData = scheduledGridApi.getDataAsCsv();
          const blob = new Blob([csvData as string], {
            type: 'text/csv;charset=utf-8;',
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `ScheduledSemen-${selectedDate}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          await scheduledGridApi.setColumnsVisible(
            [firstColumnId as string],
            true,
          );
        }
      }
    } catch (error) {
      throw new Error(`Error exporting semen: ${error}`);
    } finally {
      setIsExporting(false);
    }
  };

  async function handlePullSemen() {
    if (sequentialMovesEnabled && moveInProgress.current) return;

    scheduledGridRef.current?.api?.stopEditing();

    if (!scheduledSemen || !selectedDate || !selectedSiteId) {
      return;
    }

    const rowsToUpdate = scheduledSemen.filter(
      (row) => row.moveNum && row.moveNum >= 1,
    );

    if (sequentialMovesEnabled) {
      moveInProgress.current = true;
      setPendingMove('pull');
    }
    try {
      const pullRow = async (row: ScheduledSemenItem) => {
        if (row.sireId && row.semenId && row.moveNum && row.accountId) {
          const formattedDate = selectedDate
            ? formatISO(new Date(selectedDate as string), {
                representation: 'date',
              })
            : '';

          await pullSemenMutation.mutateAsync({
            donorScheduleDate: formattedDate,
            fertSiteId: selectedSiteId as string,
            semenId: row.semenId as string,
            moveNum: parseInt(row.moveNum.toString(), 10),
            semenType: row.semenTypeName as string,
            accountId: row.accountId as string,
            semenOwnerId: row.accountId as string,
            dsSemenLocId: row.semenStorageId as string,
          });
        }
      };

      if (sequentialMovesEnabled) {
        for (const row of rowsToUpdate) {
          await pullRow(row);
        }
      } else {
        await Promise.all(rowsToUpdate.map(pullRow));
        refetchScheduledSemen();
        refetchPulledSemen();
      }
    } catch (error) {
      console.error('Error pulling semen:', error);
      throw error;
    } finally {
      if (sequentialMovesEnabled) {
        moveInProgress.current = false;
        setPendingMove(null);
        refetchScheduledSemen();
        refetchPulledSemen();
      }
    }
  }

  const handleReturnSemen = async () => {
    if (sequentialMovesEnabled && moveInProgress.current) return;

    if (pulledSemen) {
      const rowsToUpdate = pulledSemen.filter(
        (row) => (row.returnNum as number) >= 1,
      );
      if (selectedDate && selectedSiteId) {
        if (sequentialMovesEnabled) {
          moveInProgress.current = true;
          setPendingMove('return');
        }
        try {
          const returnRow = async (row: (typeof rowsToUpdate)[number]) => {
            if (row.semenId && row.returnNum && row.semenTypeName) {
              await returnSemenMutation.mutateAsync({
                semenId: row.semenId as string,
                returnNum: parseInt(row.returnNum.toString(), 10),
                semenType: row.semenTypeName as string,
              });
            }
          };

          if (sequentialMovesEnabled) {
            for (const row of rowsToUpdate) {
              await returnRow(row);
            }
          } else {
            await Promise.all(rowsToUpdate.map(returnRow));
            refetchScheduledSemen();
            refetchPulledSemen();
          }
        } catch (error) {
          console.error('Error returning semen:', error);
          throw error;
        } finally {
          if (sequentialMovesEnabled) {
            moveInProgress.current = false;
            setPendingMove(null);
            refetchScheduledSemen();
            refetchPulledSemen();
          }
        }
      }
    }
  };

  const handleRejectDiscardSemen = async () => {
    if (pulledSemen) {
      const rowsToUpdate = pulledSemen.filter(
        (row) => (row.returnNum as number) >= 1,
      );
      if (selectedDate && selectedSiteId && rejectionDescription) {
        try {
          const promises = rowsToUpdate.map(async (row) => {
            if (row.semenId && row.returnNum && row.semenTypeName) {
              return rejectDiscardSemenMutation.mutateAsync({
                semenId: row.semenId as string,
                rejectNum: parseInt(row.returnNum.toString(), 10),
                semenType: row.semenTypeName as string,
                description: rejectionDescription,
              });
            }
          });

          await Promise.all(promises);
          refetchScheduledSemen();
          refetchPulledSemen();
        } catch (error) {
          console.error('Error rejecting/discarding semen:', error);
          throw error;
        }
      }
    }
  };

  return (
    <div className="flex gap-2">
      <section className="flex gap-2">
        <PrimeButton
          size="small"
          icon="pi pi-refresh"
          onClick={() => {
            refetchScheduledSemen();
            refetchPulledSemen();
          }}
        ></PrimeButton>
        <GenerateBullLabels date={selectedDate} />
        <PrimeButton
          label={t('export.exportCsv')}
          icon="pi pi-file-excel"
          size="small"
          onClick={handleExportSemen}
          loading={isExporting}
        ></PrimeButton>
        <PrimeButton
          size="small"
          label={t('semen.openOdSemen')}
          onClick={() => setOpenODSemenDialogVisible(true)}
        ></PrimeButton>
        {openODSemenDialogVisible && (
          <OpenODSemenDialog
            openODSemenDialogVisible={openODSemenDialogVisible}
            setOpenODSemenDialogVisible={setOpenODSemenDialogVisible}
          />
        )}
        {sslQcEnabled && (
          <PrimeButton
            size="small"
            label="SSL QC"
            icon="pi pi-check-circle"
            onClick={() => setSslQcDialogVisible(true)}
            disabled={!hasRsSemenTypes}
          />
        )}
        {sslQcDialogVisible && (
          <SslQcDialog
            visible={sslQcDialogVisible}
            setVisible={setSslQcDialogVisible}
            date={selectedDate}
            siteId={selectedSiteId}
          />
        )}
      </section>
      <section className="flex gap-2">
        <PrimeButton
          label={t('semen.pullSemen')}
          type="button"
          icon="pi pi-arrow-up"
          className="p-button-success"
          size="small"
          onClick={handlePullSemen}
          loading={
            (isMovePending && pendingMove === 'pull') ||
            pullSemenMutation.isPending
          }
          disabled={isMovePending || !semenState.pullable}
        ></PrimeButton>
        <PrimeButton
          label={t('semen.returnSemen')}
          icon="pi pi-arrow-down"
          className="p-button-warning"
          size="small"
          type="button"
          onClick={handleReturnSemen}
          disabled={isMovePending || !semenState.returnable}
          loading={
            (isMovePending && pendingMove === 'return') ||
            returnSemenMutation.isPending
          }
        ></PrimeButton>
        <PrimeButton
          label={t('semen.discardSemen')}
          icon="pi pi-trash"
          className="p-button-danger"
          size="small"
          type="button"
          onClick={() => setRejectDialogVisible(true)}
          disabled={isMovePending || !semenState.rejectable}
          loading={rejectDiscardSemenMutation.isPending}
        ></PrimeButton>
        {rejectDialogVisible && (
          <Dialog
            header={t('rejectionDescription.rejectionDescription')}
            visible={rejectDialogVisible}
            style={{ width: '50vw' }}
            onHide={() => {
              if (!rejectDialogVisible) return;
              setRejectDialogVisible(false);
            }}
            className="[&_.p-dialog-content]:flex [&_.p-dialog-content]:flex-col [&_.p-dialog-content]:items-center [&_.p-dialog-content]:justify-center [&_.p-dialog-content]:gap-4 [&_.p-dialog-title]:flex [&_.p-dialog-title]:items-center [&_.p-dialog-title]:justify-center [&_.p-inputtext]:w-120"
          >
            <InputText
              value={rejectionDescription}
              onChange={(e) => setRejectionDescription(e.target.value)}
            />
            <div className="mb-4 flex gap-4">
              <PrimeButton
                label={t('common.cancel')}
                onClick={() => {
                  setRejectDialogVisible(false);
                }}
              />
              <PrimeButton
                label={t('common.submit')}
                onClick={() => {
                  setRejectDialogVisible(false);
                  handleRejectDiscardSemen();
                }}
                disabled={!rejectionDescription}
              />
            </div>
          </Dialog>
        )}
      </section>
    </div>
  );
};
